/** The channel list (UX-SPEC §7.1): every channel, always, with the translation axis in its own group. */
import { signal } from '@preact/signals';
import { Fragment } from 'preact';
import type { ComponentChildren } from 'preact';
import { CHANNEL_EXPLAINERS } from '../../engine/channels/explain.ts';
import type { ChannelRow } from '../../engine/ensemble/index.ts';
import { REFERENCE_SIGN_NOTE } from '../../engine/ensemble/wording.ts';
import { FAMILY_KEY, type GlossaryKey } from '../../shared/help-types.ts';
import {
	type HumanComparison,
	compareHuman,
	compareToHuman,
} from '../../shared/human-reference.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import {
	HumanCell,
	HumanCountLine,
	HumanLegend,
	HumanRange,
	HumanSelector,
	humanStrata,
	setHumanStrata,
	useHumanCounts,
	useHumanReference,
} from '../bench/HumanCompare.tsx';
import { ChannelTerm, Term } from '../components/Term.tsx';
import { focus, focusReplaced, toggleFocus } from '../focus.ts';
import { fmt1, fmt2, fmtInt, fmtInterval, fmtSigned1 } from '../format.ts';
import { derive } from '../model.ts';
import { hashFor } from '../router.ts';
import { HUMAN_REF as HR } from '../strings-bench.ts';
import { DIRECTION, HEADLINE_EXPLAIN } from '../strings-explain.ts';
import { glossaryFor } from '../strings-glossary.ts';
import { CHANNELS as C, OVERVIEW, TRANSLATION as TR } from '../strings.ts';
import { Notice, Seg, Whisker } from '../ui.tsx';

export type SortKey = 'score' | 'deviation' | 'kappa' | 'hotspots' | 'id' | 'human';
const sortKey = signal<SortKey>('score');
const grouped = signal(true);

const TERM_KEYS: GlossaryKey[] = [
	'evidence-strength',
	'interval-90',
	'kappa',
	'deviation',
	'hotspot',
	'elevated',
	'evidence-span',
	'little-evidence',
	'hollow-whisker',
	'withheld',
	'abstained',
	'reads-other-way',
];

/** The explainer keys of the translation group (MT-01, TR-01, TR-02): hover text on the name and, for MT-01, its standing label. */
const TR_IDS = ['MT-01', 'TR-01', 'TR-02'] as const;
export const isTranslationExplained = (id: string): id is (typeof TR_IDS)[number] =>
	(TR_IDS as readonly string[]).includes(id);

export const ED14 =
	'length-dependent until the channel priors are replaced by corpus values (DESIGN ED-14)';

/** The score with its ED-14 note beside it on the same line (UX-CH-07), and "little evidence" for κ < 0.2 (UX-CH-04). */
export function ScoreCell({ row }: { row: ChannelRow }) {
	if (row.status === 'abstain') return <span>{C.abstained(row.reason ?? '')}</span>;
	return (
		<span class="score-cell">
			<span class="num strong">{fmt1(row.score ?? 0)}</span>
			{(row.kappa ?? 1) < 0.2 ? (
				<span class="note">
					<Term k="little-evidence">{C.littleEvidence}</Term>
				</span>
			) : null}
			{row.notes.includes(ED14) ? <span class="note-notice">{ED14}</span> : null}
		</span>
	);
}

function valueFor(row: ChannelRow, key: SortKey, hot: number): number | string | undefined {
	switch (key) {
		case 'score':
			return row.score;
		case 'deviation':
			return row.deviation;
		case 'kappa':
			return row.kappa;
		case 'hotspots':
			return row.flagsWithheld ? undefined : hot;
		case 'id':
			return row.id;
		case 'human':
			return undefined;
	}
}

const NOT_APPLICABLE: HumanComparison = { status: 'not-applicable' };

function sortRows(
	rows: ChannelRow[],
	key: SortKey,
	hotOf: (id: ChannelRow['id']) => number,
	humanOf: (id: ChannelRow['id']) => HumanComparison | null
): ChannelRow[] {
	return [...rows].sort((a, b) => {
		if (key === 'id') return a.id < b.id ? -1 : 1;
		// the distance from human writing, the farthest first; unmeasured and not-applicable last (UD-24)
		if (key === 'human')
			return (
				compareHuman(humanOf(a.id) ?? NOT_APPLICABLE, humanOf(b.id) ?? NOT_APPLICABLE) ||
				(a.id < b.id ? -1 : 1)
			);
		const av = a.status === 'abstain' ? undefined : valueFor(a, key, hotOf(a.id));
		const bv = b.status === 'abstain' ? undefined : valueFor(b, key, hotOf(b.id));
		// abstained (and withheld, for the hotspot count) rows sort last in every numeric sort
		if (av === undefined && bv === undefined) return a.id < b.id ? -1 : 1;
		if (av === undefined) return 1;
		if (bv === undefined) return -1;
		return (bv as number) - (av as number) || (a.id < b.id ? -1 : 1);
	});
}

function Row({
	vm,
	row,
	human,
}: { vm: ViewModel; row: ChannelRow; human: HumanComparison | null | undefined }) {
	const d = derive(vm);
	const detail = d.detail.get(row.id);
	const hot = d.hotspotsByChannel.get(row.id) ?? 0;
	const focused = focus.value.includes(row.id);
	const ci = row.ci90;
	const dir = detail
		? detail.direction > 0
			? 'higher'
			: 'lower'
		: CHANNEL_EXPLAINERS[row.id]?.direction;
	return (
		<tr data-channel={row.id} class={row.status === 'abstain' ? 'is-abstain' : undefined}>
			<td data-label={C.cols.focus}>
				<input
					type="checkbox"
					class="check"
					checked={focused}
					aria-label={C.focusFor(row.id)}
					onChange={() => toggleFocus(row.id)}
				/>
			</td>
			<th scope="row" class="mono" data-label={C.cols.id}>
				<a href={hashFor('channel', { id: row.id })}>{row.id}</a>
			</th>
			<td
				data-label={C.cols.name}
				title={isTranslationExplained(row.id) ? TR.tip(TR[row.id].measures) : undefined}
			>
				<ChannelTerm id={row.id} name={row.name} />
				{dir ? (
					<span
						class="dir-badge"
						role="img"
						aria-label={dir === 'higher' ? DIRECTION.higher : DIRECTION.lower}
					>
						{dir === 'higher' ? DIRECTION.higherShort : DIRECTION.lowerShort}
					</span>
				) : null}
			</td>
			<td data-label={C.cols.family} class="dim">
				{FAMILY_KEY[row.family] ? (
					<Term k={FAMILY_KEY[row.family] as GlossaryKey}>{row.family}</Term>
				) : (
					row.family
				)}
			</td>
			<td data-label={C.cols.score}>
				<ScoreCell row={row} />
			</td>
			<td data-label={C.cols.interval}>
				{row.status === 'scored' && ci ? (
					<span class="ci-cell">
						<span class="num">{fmtInterval(ci)}</span>
						<Whisker
							value={row.score ?? 0}
							lo={ci[0]}
							hi={ci[1]}
							width={96}
							hollow={(row.kappa ?? 1) < 0.2}
						/>
					</span>
				) : (
					''
				)}
			</td>
			{human !== undefined ? (
				<>
					<td data-label={HR.cols.doc} class="hr-col">
						<HumanCell c={human} testId={`hr-doc-${row.id}`} />
					</td>
					<td data-label={HR.cols.docRange} class="hr-col">
						<HumanRange c={human} help={HR.docRangeHelp} />
					</td>
				</>
			) : null}
			<td class="num" data-label={C.cols.kappa}>
				{row.kappa === undefined ? '' : fmt2(row.kappa)}
			</td>
			<td class="num" data-label={C.cols.deviation}>
				{row.deviation === undefined ? '' : fmtSigned1(row.deviation)}
			</td>
			<td class="num" data-label={C.cols.hotspots}>
				{row.flagsWithheld ? (
					<span class="note-notice" title={row.flagsWithheld}>
						<Term k="withheld">{C.flagsWithheld}</Term>
						<span class="visually-hidden">: {row.flagsWithheld}</span>
					</span>
				) : hot > 0 ? (
					<a href={hashFor('hotspots', { params: { ch: row.id } })}>{fmtInt(hot)}</a>
				) : (
					fmtInt(0)
				)}
			</td>
			<td class="num" data-label={C.cols.spans}>
				{fmtInt(detail?.spanCount ?? 0)}
			</td>
			<td data-label={C.cols.notes}>
				<span class="notes-cell">
					{row.notes
						.filter((n) => n !== ED14)
						.map((n) => (
							<span key={n} class="note">
								{n}
							</span>
						))}
					{row.referenceNote ? (
						<span class="note" data-testid="reference-note">
							{row.referenceNote}
						</span>
					) : null}
					{row.referenceNote === REFERENCE_SIGN_NOTE.inverted ? (
						<span class="note">{DIRECTION.readsOtherWay}</span>
					) : null}
					{row.id === 'MT-01' ? (
						<span class="note" data-testid="no-value-label">
							{TR.noValue}
						</span>
					) : null}
				</span>
			</td>
		</tr>
	);
}

function Head({ human }: { human: boolean }) {
	const k = sortKey.value;
	return (
		<thead>
			<tr>
				<th scope="col">{C.cols.focus}</th>
				<th scope="col" aria-sort={k === 'id' ? 'ascending' : 'none'}>
					<button
						type="button"
						class="th-sort"
						onClick={() => {
							sortKey.value = 'id';
						}}
					>
						{C.cols.id}
					</button>
				</th>
				<th scope="col">{C.cols.name}</th>
				<th scope="col">{C.cols.family}</th>
				<th scope="col" aria-sort={k === 'score' ? 'descending' : 'none'}>
					<button
						type="button"
						class="th-sort"
						onClick={() => {
							sortKey.value = 'score';
						}}
					>
						{C.cols.score}
					</button>
				</th>
				<th scope="col">
					<Term k="interval-90">{C.cols.interval}</Term>
				</th>
				{human ? (
					<>
						<th scope="col" aria-sort={k === 'human' ? 'descending' : 'none'}>
							<button
								type="button"
								class="th-sort"
								onClick={() => {
									sortKey.value = 'human';
								}}
							>
								{HR.cols.doc}
							</button>
						</th>
						<th scope="col">{HR.cols.docRange}</th>
					</>
				) : null}
				<th scope="col" class="num" aria-sort={k === 'kappa' ? 'descending' : 'none'}>
					<button
						type="button"
						class="th-sort"
						onClick={() => {
							sortKey.value = 'kappa';
						}}
					>
						{C.cols.kappa}
					</button>
				</th>
				<th scope="col" class="num" aria-sort={k === 'deviation' ? 'descending' : 'none'}>
					<button
						type="button"
						class="th-sort"
						onClick={() => {
							sortKey.value = 'deviation';
						}}
					>
						{C.cols.deviation}
					</button>
				</th>
				<th scope="col" class="num" aria-sort={k === 'hotspots' ? 'descending' : 'none'}>
					<button
						type="button"
						class="th-sort"
						onClick={() => {
							sortKey.value = 'hotspots';
						}}
					>
						{C.cols.hotspots}
					</button>
				</th>
				<th scope="col" class="num">
					<Term k="evidence-span">{C.cols.spans}</Term>
				</th>
				<th scope="col">{C.cols.notes}</th>
			</tr>
		</thead>
	);
}

export function ChannelsView({ vm }: { vm: ViewModel }) {
	const d = derive(vm);
	const rows = vm.ensemble.channels;
	const hotOf = (id: ChannelRow['id']) => d.hotspotsByChannel.get(id) ?? 0;
	const translation = rows.filter((r) => r.axis === 'translation');
	const auth = rows.filter((r) => r.axis === 'authorship');
	const families = [...new Set(auth.map((r) => r.family))];
	const key = sortKey.value;
	const focusIds = focus.value;
	// each channel against human writing of about this length, in the viewer's categories (UD-24)
	const human = useHumanReference();
	const sel = human.ref ? humanStrata.value : null;
	const words = vm.meta.words;
	const comparisons = new Map(
		rows.map((r) => [
			r.id,
			human.ref && sel
				? compareToHuman(
						human.ref,
						r.id,
						r.status === 'scored' && r.score !== undefined ? r.score : null,
						words,
						sel
					)
				: null,
		])
	);
	// how many channels are red and outside, against human documents of this length (UD-26)
	const counts = useHumanCounts(
		human.ref,
		rows.map((r) => comparisons.get(r.id) ?? null),
		words,
		sel
	);
	// the columns stay while the reference loads ("…"), and go if it cannot be loaded
	const showHuman = !human.error;
	const humanOf = (id: ChannelRow['id']) => comparisons.get(id) ?? null;
	const body = (list: ChannelRow[]) =>
		sortRows(list, key, hotOf, humanOf).map((r) => (
			<Row key={r.id} vm={vm} row={r} human={showHuman ? humanOf(r.id) : undefined} />
		));
	const groupRow = (label: ComponentChildren) => (
		<tr class="grouprow">
			<th scope="colgroup" colSpan={showHuman ? 13 : 11}>
				{label}
			</th>
		</tr>
	);
	return (
		<div class="wrap wrap-wide" data-testid="channels">
			<div class="sec-head">
				<h1>{C.title}</h1>
				<Seg label={C.group}>
					<button
						type="button"
						class={`seg-btn${grouped.value ? ' is-on' : ''}`}
						aria-pressed={grouped.value}
						onClick={() => {
							grouped.value = true;
						}}
					>
						{C.groupFamily}
					</button>
					<button
						type="button"
						class={`seg-btn${!grouped.value ? ' is-on' : ''}`}
						aria-pressed={!grouped.value}
						onClick={() => {
							grouped.value = false;
						}}
					>
						{C.groupNone}
					</button>
				</Seg>
			</div>
			<p class="caption">{C.intro}</p>
			<p class="caption">
				{OVERVIEW.hotspotDef}. {OVERVIEW.elevatedDef}.
			</p>
			{translation.length > 0 ? (
				<p class="caption" data-testid="translation-group-note">
					{TR.groupNote} {TR.scope}
				</p>
			) : null}
			<p class="caption">
				{DIRECTION.hollowLegend} · {DIRECTION.filledLegend}
			</p>
			<p class="caption" data-testid="channels-terms">
				{HEADLINE_EXPLAIN.termsLabel}:{' '}
				{TERM_KEYS.map((k, i) => (
					<Fragment key={k}>
						{i > 0 ? ' · ' : null}
						<Term k={k}>{glossaryFor(k).term}</Term>
					</Fragment>
				))}
			</p>
			{focusReplaced.value ? <output class="note-notice">{C.focusCap}</output> : null}
			{focusIds.length > 0 ? (
				<p class="caption" data-testid="focus-line">
					{focusIds.join(', ')} ·{' '}
					<a href={hashFor('read', { params: { ch: focusIds.join(',') } })}>{C.openReader}</a>
				</p>
			) : null}
			<section class="hr" aria-label={HR.title} data-testid="hr">
				<p class="caption">{HR.help}</p>
				{human.ref && sel ? (
					<>
						<HumanSelector reference={human.ref} selected={sel} onChange={setHumanStrata} />
						{sel.size === 0 ? (
							<Notice kind="info">{HR.noneSelected}</Notice>
						) : (
							<>
								<HumanLegend />
								<HumanCountLine summary={counts} testId="hr-counts" />
							</>
						)}
					</>
				) : (
					<p class="caption" data-testid="hr-status">
						{human.error ? HR.failed : HR.loading}
					</p>
				)}
			</section>
			<div class="tablewrap cards-on-mobile">
				<table data-testid="channels-table">
					<caption class="visually-hidden">{C.title}</caption>
					<Head human={showHuman} />
					<tbody>
						{translation.length > 0 ? groupRow(C.translationGroup) : null}
						{body(translation)}
						{grouped.value ? (
							families.map((f) => (
								<Fragment key={f}>
									{groupRow(
										FAMILY_KEY[f] ? (
											<Term
												k={FAMILY_KEY[f] as GlossaryKey}
											>{`${f[0]}${f.slice(1).toLowerCase()}`}</Term>
										) : (
											`${f[0]}${f.slice(1).toLowerCase()}`
										)
									)}
									{body(auth.filter((r) => r.family === f))}
								</Fragment>
							))
						) : (
							<>
								{groupRow(C.authorshipGroup)}
								{body(auth)}
							</>
						)}
					</tbody>
				</table>
			</div>
			<p class="caption" data-testid="hr-caveat">
				{HR.caveat}
				{human.ref && sel
					? ` ${HR.source(fmtInt(human.ref.strata.reduce((n, s) => n + (sel.has(s.id) ? s.n : 0), 0)))}`
					: ''}
			</p>
		</div>
	);
}
